import React from 'react';
import PropTypes from 'prop-types';
import {TimerDisplay} from '../TimerDisplay';
import {ButtonControls} from '../ButtonControls';
import {StartStopButton} from '../StartStopButton';
import {throttle} from '@learningblocks/exp-constants/lib/Utilities';
import {MCStateContext} from '../../../..';

import './style.scss';
import {useTranslation} from 'react-i18next';

const HOUR_RATE = 1;
const MINUTE_RATE = 30;
const LOWERBOUND_TIME = new Date(0, 0, 0).getTime();
const TRANSLATION_KEY = {
  TIMER: 'Timer.string',
  HH: 'HH.string',
  MM: 'MM.string',
  SS: 'SS.string',
};

export function TimerBlock(props) {
  const {pulsing, running, balanced, isLidOpen} =
    React.useContext(MCStateContext);

  const incrementHours = () => {
    let time = new Date(props.value.getTime());
    time.setHours(time.getHours() + HOUR_RATE);
    props.setTimer(time);
  };
  const decrementHours = () => {
    if (props.value.getTime() === LOWERBOUND_TIME)
      props.setTimer(new Date(LOWERBOUND_TIME));
    else {
      let time = new Date(props.value.getTime());
      time.setHours(time.getHours() - HOUR_RATE);
      props.setTimer(time);
    }
  };

  const incrementMinutes = () => {
    let time = new Date(props.value.getTime());
    time.setMinutes(time.getMinutes() + MINUTE_RATE);
    props.setTimer(time);
  };
  const decrementMinutes = () => {
    if (props.value.getTime() === LOWERBOUND_TIME)
      props.setTimer(new Date(LOWERBOUND_TIME));
    else {
      let time = new Date(props.value.getTime());
      time.setMinutes(time.getMinutes() - MINUTE_RATE);
      props.setTimer(time);
    }
  };

  const throttleCallback = () => {
    if (props.value.getTime() === LOWERBOUND_TIME) props.onStopCentrifuge();
    else {
      let time = new Date(props.value.getTime());
      time.setSeconds(time.getSeconds() - 1);
      props.setTimer(time);
    }
  };

  React.useEffect(() => {
    if (running) {
      setTimeout(() => {
        throttleCallback();
      }, props.timeInterval);
    }
  }, [running, props.value]);

  const startStopActive =
    !pulsing &&
    balanced &&
    !isLidOpen &&
    props.value.getTime() != LOWERBOUND_TIME;

  const onClickStart = () => {
    startStopActive && props.onRunCentrifuge();
  };

  const onClickStop = () => {
    !pulsing && props.onStopCentrifuge();
  };

  const {t} = useTranslation();

  let time = new Date(props.value.getTime());
  const hours = time.getHours();
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();

  return (
    <div className='mc_settings_timer'>
      <h5 className='mc_settings_timer_title'>{t(TRANSLATION_KEY.TIMER)}</h5>
      <TimerDisplay timeData={props.value} setTimer={props.setTimer} />
      <div className='mc_settings_timer_controlBlock'>
        <ButtonControls
          text={t(TRANSLATION_KEY.HH)}
          increment={incrementHours}
          decrement={decrementHours}
          altText1={`Increase hour button. Current time set to  ${hours} hour  ${minutes} minutes and ${seconds} seconds. Select to increase the time by 1 hour.`}
          altText2={`Increase hour button. Current time set to  ${hours} hour  ${minutes} minutes and ${seconds} seconds. Select to decrease the time by 1 hour.`}
        />
        <ButtonControls
          text={t(TRANSLATION_KEY.MM)}
          increment={incrementMinutes}
          decrement={decrementMinutes}
          altText1={`Increase minutes button. Current time set to ${hours} hour ${minutes} minutes and  ${seconds} seconds. Select to increase the time by 30 minutes.`}
          altText2={`Decrease minutes button. Current time set to ${hours} hour ${minutes} minutes and  ${seconds} seconds. Select to decrease the time by 30 minutes.`}
        />
      </div>
      <StartStopButton
        onClickStart={onClickStart}
        onClickStop={onClickStop}
        disabled={!startStopActive}
      />
    </div>
  );
}

TimerBlock.propTypes = {
  /**
   * timer value to be displayed
   */
  value: PropTypes.instanceOf(Date),
  /**
   * callback function to set timer value
   */
  setTimer: PropTypes.func,
  /**
   * callback function to start centrifuge
   */
  onRunCentrifuge: PropTypes.func,
  /**
   * callback function stop centrifuge
   */
  onStopCentrifuge: PropTypes.func,
};

TimerBlock.defaultProps = {
  timeInterval: 500,
};
